import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePresentation } from '../hooks/usePresentation.js';
import { useAuth } from '../contexts/AuthContext.jsx';
import ToolHeader from '../components/ToolHeader.jsx';
import {
  generateSlides,
  SLIDE_TYPES,
  downloadPptx,
  TEMPLATES,
  getTemplate,
  makeVisualSlide,
  isInsertedSlide,
} from './builderSlides.js';
import './Builder.css';

/**
 * 04 · Visuals (assembles + downloads the deck; internal route stays /tools/builder)
 *
 * Auto-generates a slide deck from the user's Draft content.
 * The user can:
 *   - flip through every slide (preview)
 *   - toggle between dark and light slide theme
 *   - pick a template (fonts + colors)
 *   - make light edits to title / body / notes for any slide (overrides
 *     apply just to this slide; the underlying Draft data is untouched)
 *   - insert blank "visual" slides for photos / charts / diagrams
 *   - delete inserted slides
 *   - download the deck as a .pptx file
 *
 * The deck Presentation Lab produces is a STARTING POINT, by design — a
 * clean, working skeleton meant to be downloaded and polished (or handed
 * to an AI design tool). The banner at the top of the screen says so;
 * editing here is for light touch-ups, not the final pass.
 */

/**
 * Decide whether a template should be shown to the current user.
 *
 * Most templates are unrestricted (no `restricted` field) and show to
 * everyone. A template with `restricted: '@somedomain'` only appears
 * for users whose email ends with that domain — e.g. the Nova Scotia
 * Power template is restricted to '@nspower.ca'.
 *
 * The match is case-insensitive and trims whitespace, so 'Allie@NSPower.ca '
 * still matches '@nspower.ca'. Users who aren't signed in, or whose email
 * doesn't match, simply never see the restricted template.
 */
function isTemplateAvailable(template, email) {
  if (!template.restricted) return true; // unrestricted → everyone
  if (typeof email !== 'string') return false;
  const normalized = email.trim().toLowerCase();
  return normalized.endsWith(template.restricted.toLowerCase());
}

export default function Builder() {
  const { data, updateTool, savedFlash } = usePresentation();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Templates available to THIS user. Restricted templates (e.g. the
  // Nova Scotia Power template, gated to @nspower.ca) are filtered out
  // for everyone else, so they never appear in the gallery.
  const availableTemplates = useMemo(
    () => TEMPLATES.filter((t) => isTemplateAvailable(t, user?.email)),
    [user?.email]
  );

  // Generate the base deck (auto + inserted) from draft data.
  const generated = useMemo(() => generateSlides(data), [data]);

  // Per-slide edits live in builder state, keyed by slide.id
  const overrides = data.builder?.overrides ?? {};
  const insertedSlides = data.builder?.insertedSlides ?? [];
  const theme = data.builder?.theme ?? 'dark';

  // Resolve the chosen template. If the user previously selected a
  // template they can no longer access (e.g. their saved templateId is
  // 'nspower' but they're not on an @nspower.ca email), fall back to the
  // default so a restricted look can't "stick" via stale saved state.
  const savedTemplateId = data.builder?.templateId ?? 'lab-default';
  const templateId = availableTemplates.some((t) => t.id === savedTemplateId)
    ? savedTemplateId
    : 'lab-default';
  const activeTemplate = getTemplate(templateId);

  // Apply overrides on top of generated slides.
  const slides = useMemo(
    () =>
      generated.map((s) => {
        const override = overrides[s.id] ?? {};
        const merged = { ...s };
        for (const [key, value] of Object.entries(override)) {
          if (value !== undefined && value !== null && value !== '') {
            merged[key] = value;
          }
        }
        return merged;
      }),
    [generated, overrides]
  );

  const [idx, setIdx] = useState(0);
  const safeIdx = Math.min(idx, slides.length - 1);
  const slide = slides[safeIdx];

  const [downloading, setDownloading] = useState(false);

  function setOverride(slideId, patch) {
    updateTool('builder', {
      overrides: { ...overrides, [slideId]: { ...overrides[slideId], ...patch } },
    });
  }

  function setTheme(t) {
    updateTool('builder', { theme: t });
  }

  function setTemplateId(id) {
    updateTool('builder', { templateId: id });
  }

  function clearOverride(slideId) {
    const next = { ...overrides };
    delete next[slideId];
    updateTool('builder', { overrides: next });
  }

  function clearAllOverrides() {
    const count = Object.keys(overrides).length;
    if (count === 0) return;
    const ok = confirm(
      `Wipe ${count} manual slide ${count === 1 ? 'edit' : 'edits'} and rebuild every slide ` +
      `from your latest Logistics + Draft data?`
    );
    if (ok) updateTool('builder', { overrides: {} });
  }
  const overrideCount = Object.keys(overrides).length;

  /**
   * Insert a blank visual slide immediately after the slide currently
   * being viewed. The new slide is anchored to that slide's id, so it
   * will stay in the right place even if the Draft changes other slides.
   */
  function insertVisualAfterCurrent() {
    if (!slide) return;
    const newSlide = makeVisualSlide(slide.id);
    updateTool('builder', {
      insertedSlides: [...insertedSlides, newSlide],
    });
    // Jump to the new slide so the user lands on what they just made.
    setIdx(safeIdx + 1);
  }

  /**
   * Delete a user-inserted slide. Auto-generated slides can't be
   * deleted from here — they come from Draft data.
   */
  function deleteInsertedSlide(slideId) {
    if (!isInsertedSlide(slide)) return;
    const ok = confirm('Delete this visual slide? This can\'t be undone.');
    if (!ok) return;
    const nextInserted = insertedSlides.filter((s) => s.id !== slideId);
    const nextOverrides = { ...overrides };
    delete nextOverrides[slideId];
    updateTool('builder', {
      insertedSlides: nextInserted,
      overrides: nextOverrides,
    });
    // Stay on whatever slide now occupies the previous index, or
    // fall back to the previous slide if we deleted the last one.
    setIdx((prev) => Math.min(prev, slides.length - 2));
  }

  async function handleDownload() {
    if (downloading) return;
    setDownloading(true);
    try {
      const exported = slides.map(({ id, insertAfterSlideId, ...rest }) => rest);
      const filename = generateFilename(data);
      await downloadPptx(exported, theme, filename, templateId);
    } catch (e) {
      console.error(e);
      alert('Could not generate the .pptx — ' + (e?.message ?? 'unknown error'));
    } finally {
      setDownloading(false);
    }
  }

  if (!slide) {
    return (
      <div className="builder">
        <ToolHeader num="04" title="Visuals" savedFlash={savedFlash} />
        <div className="bd-empty">
          <p>No slides yet — build out your Draft first.</p>
          <button className="btn btn-primary" onClick={() => navigate('/tools/draft')}>
            Go to Draft
          </button>
        </div>
      </div>
    );
  }

  const typeLabel = (SLIDE_TYPES[slide.type] || SLIDE_TYPES.content).label;
  const isFirst = safeIdx === 0;
  const isLast = safeIdx === slides.length - 1;
  const isInserted = isInsertedSlide(slide);

  return (
    <div className="builder">
      <ToolHeader num="04" title="Visuals" savedFlash={savedFlash} />

      <div className="bd-body">

        {/* ── Starting-deck banner — highly visible, always shown ── */}
        <div
          className="bd-starter-banner"
          style={{
            display: 'flex',
            gap: '12px',
            alignItems: 'flex-start',
            border: '1px solid var(--terra)',
            background: 'var(--cream)',
            borderRadius: '12px',
            padding: '14px 16px',
          }}
        >
          <span aria-hidden="true" style={{ fontSize: '1.3rem', lineHeight: 1 }}>📋</span>
          <div>
            <strong
              style={{
                display: 'block',
                fontFamily: 'var(--font-display, serif)',
                fontSize: '1.05rem',
                marginBottom: '4px',
                color: 'var(--ink)',
              }}
            >
              This is a starting deck — not the finished one.
            </strong>
            <span style={{ fontSize: '0.9rem', lineHeight: 1.5, color: 'var(--ink)', opacity: 0.85 }}>
              Presentation Lab builds a clean, working draft from your plan: every slide in order,
              your speaker notes attached. Download it and make it yours — tighten the wording, add
              your visuals and data, or drop this copy into an AI design tool to turn it into a
              polished deck. What you download is a first draft to build on, not the final
              presentation.
            </span>
          </div>
        </div>

        {/* Theme toggle + refresh */}
        <div className="bd-toolbar">
          <div className="bd-theme">
            <span className="eyebrow">Slide theme</span>
            <div className="bd-theme-toggle">
              <button
                type="button"
                className={`bd-theme-btn ${theme === 'dark' ? 'is-on' : ''}`}
                onClick={() => setTheme('dark')}
              >Dark</button>
              <button
                type="button"
                className={`bd-theme-btn ${theme === 'light' ? 'is-on' : ''}`}
                onClick={() => setTheme('light')}
              >Light</button>
            </div>
          </div>
          {overrideCount > 0 && (
            <button
              type="button"
              className="bd-refresh-btn"
              onClick={clearAllOverrides}
              title="Wipe all manual slide edits and rebuild from latest data"
            >
              ↻ Refresh slides from data
              <span className="bd-refresh-count">{overrideCount} edited</span>
            </button>
          )}
        </div>

        {/* Template gallery */}
        <div className="bd-templates">
          <span className="eyebrow">Template</span>
          <div className="bd-template-grid">
            {availableTemplates.map((t) => {
              const previewBg = theme === 'dark' ? t.bgDark : t.bgLight;
              const previewTitleColor = theme === 'dark' ? t.titleColorDark : t.titleColorLight;
              const previewBodyColor = theme === 'dark' ? t.bodyColorDark : t.bodyColorLight;
              const isSelected = t.id === templateId;
              return (
                <button
                  key={t.id}
                  type="button"
                  className={`bd-template-card ${isSelected ? 'is-selected' : ''}`}
                  onClick={() => setTemplateId(t.id)}
                >
                  <div
                    className="bd-template-thumb"
                    style={{ background: `#${previewBg}` }}
                  >
                    <div
                      className="bd-template-thumb-title"
                      style={{
                        color: `#${previewTitleColor}`,
                        fontFamily: `"${t.headingFont}", sans-serif`,
                      }}
                    >
                      Sample Title
                    </div>
                    <div
                      className="bd-template-thumb-body"
                      style={{
                        color: `#${previewBodyColor}`,
                        fontFamily: `"${t.bodyFont}", sans-serif`,
                      }}
                    >
                      Body text appears here
                    </div>
                    {/* Footer preview: NSP-style templates show a solid bar;
                        all others show the two thin accent stripes. */}
                    {t.footerStyle === 'logoBar' ? (
                      <div
                        className="bd-template-thumb-bar"
                        style={{ background: `#${t.footerBarColor || t.accentLeft}` }}
                      />
                    ) : (
                      <div className="bd-template-thumb-stripe">
                        <span style={{ background: `#${t.accentLeft}` }} />
                        <span style={{ background: `#${t.accentRight}` }} />
                      </div>
                    )}
                  </div>
                  <div className="bd-template-meta">
                    <div className="bd-template-name">{t.name}</div>
                    <div className="bd-template-desc">{t.desc}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Slide preview — layout switches by slide.type */}
        <SlidePreview
          slide={slide}
          theme={theme}
          template={activeTemplate}
          typeLabel={typeLabel}
        />

        {/*
          Always-visible nudge about slide density. We only show it on
          slide types where text overflow is a real risk (content,
          agenda, objective, closing, pause). Visual / section / title
          slides aren't text-heavy so the nudge would just be noise.
        */}
        {['content', 'agenda', 'objective', 'closing', 'pause'].includes(slide.type) && (
          <div className="bd-density-tip">
            <span className="bd-density-tip-icon" aria-hidden="true">💡</span>
            <span>
              <strong>Slide too dense?</strong> If text spills past the slide edges
              or feels cramped, shorten the point back in{' '}
              <button
                type="button"
                className="bd-density-tip-link"
                onClick={() => navigate('/tools/draft')}
              >Draft</button>{' '}
              — one short line per point reads best.
            </span>
          </div>
        )}

        {/* Slide nav */}
        <div className="bd-nav">
          <button
            type="button"
            className="bd-nav-btn"
            disabled={isFirst}
            onClick={() => setIdx(safeIdx - 1)}
          >← Prev</button>
          <span className="eyebrow">
            Slide {safeIdx + 1} of {slides.length}
          </span>
          <button
            type="button"
            className="bd-nav-btn"
            disabled={isLast}
            onClick={() => setIdx(safeIdx + 1)}
          >Next →</button>
        </div>

        {/* Insert button — sits between nav and edit panel so it's
            always within thumb's reach as users flip through. */}
        <button
          type="button"
          className="bd-insert-btn"
          onClick={insertVisualAfterCurrent}
          title="Insert a blank visual slide immediately after this one"
        >
          + Insert visual slide after this
        </button>

        {/* Edit fields */}
        <div className="bd-edit">
          <h3>Edit this slide</h3>

          {slide.type === 'visual' ? (
            // Visual slides have minimal editable text — just an
            // optional caption and speaker notes. The body field
            // becomes the placeholder hint shown until the user
            // adds an image in PowerPoint after download.
            <>
              <div className="bd-field">
                <label className="eyebrow" htmlFor="bd-body">
                  Placeholder caption
                </label>
                <input
                  id="bd-body"
                  type="text"
                  value={slide.body}
                  onChange={(e) => setOverride(slide.id, { body: e.target.value })}
                  placeholder="Replace this with a photo, chart, or diagram"
                />
              </div>
              <div className="bd-field">
                <label className="eyebrow" htmlFor="bd-notes">Speaker notes</label>
                <textarea
                  id="bd-notes"
                  value={slide.notes ?? ''}
                  onChange={(e) => setOverride(slide.id, { notes: e.target.value })}
                  placeholder="What you'll say while this visual is on screen"
                  rows={3}
                />
              </div>
            </>
          ) : (
            <>
              <div className="bd-field">
                <label className="eyebrow" htmlFor="bd-title">Title</label>
                <input
                  id="bd-title"
                  type="text"
                  value={slide.title}
                  onChange={(e) => setOverride(slide.id, { title: e.target.value })}
                  placeholder="Slide title"
                />
              </div>

              <div className="bd-field">
                <label className="eyebrow" htmlFor="bd-body">
                  {slide.type === 'content' ? 'Bullets (one per line)' : 'Body'}
                </label>
                <textarea
                  id="bd-body"
                  value={slide.body}
                  onChange={(e) => setOverride(slide.id, { body: e.target.value })}
                  placeholder={
                    slide.type === 'content'
                      ? 'One bullet per line'
                      : 'What goes on this slide'
                  }
                  rows={slide.type === 'content' ? 6 : 4}
                />
              </div>

              <div className="bd-field">
                <label className="eyebrow" htmlFor="bd-notes">Speaker notes</label>
                <textarea
                  id="bd-notes"
                  value={slide.notes ?? ''}
                  onChange={(e) => setOverride(slide.id, { notes: e.target.value })}
                  placeholder="What you'll say but don't want on the slide"
                  rows={4}
                />
              </div>
            </>
          )}

          {/* Action row: reset (auto slides) or delete (inserted slides) */}
          <div className="bd-edit-actions">
            {!isInserted && overrides[slide.id] && (
              <button
                type="button"
                className="btn-text"
                onClick={() => clearOverride(slide.id)}
                title="Discard your edits to this slide and rebuild it from your Logistics + Draft data"
              >
                ↺ Reset this slide to use my latest data
              </button>
            )}
            {isInserted && (
              <button
                type="button"
                className="btn-text danger"
                onClick={() => deleteInsertedSlide(slide.id)}
              >
                × Delete this visual slide
              </button>
            )}
          </div>
        </div>

        {/* Download */}
        <button
          type="button"
          className="btn btn-primary bd-download"
          onClick={handleDownload}
          disabled={downloading}
        >
          {downloading ? 'Building your .pptx…' : 'Download .pptx →'}
        </button>

        <div className="bd-foot">
          <p>
            Remember: this is your <strong>starting deck</strong>, not the final one. Open the
            downloaded file in PowerPoint or Keynote to polish it, replace placeholders with
            visuals, and adjust before delivering — or drop this copy into an AI design tool to
            build a finished look from it.
          </p>
        </div>

        <button
          type="button"
          className="btn btn-secondary bd-back"
          onClick={() => navigate('/')}
        >
          Save &amp; back to home
        </button>
      </div>
    </div>
  );
}

/* ─────────────── Slide preview component ─────────────── */
/**
 * Renders one slide using a layout that depends on slide.type. Visual
 * slides show a dashed placeholder; section slides center their title;
 * content slides render the body as bullets.
 */
function SlidePreview({ slide, theme, template, typeLabel }) {
  const bgColor = `#${theme === 'dark' ? template.bgDark : template.bgLight}`;
  const titleColor = `#${theme === 'dark' ? template.titleColorDark : template.titleColorLight}`;
  const bodyColor = `#${theme === 'dark' ? template.bodyColorDark : template.bodyColorLight}`;
  const headingFontStack = `"${template.headingFont}", serif`;
  const bodyFontStack = `"${template.bodyFont}", sans-serif`;

  // Footer preview: NSP-style templates render a solid bar with the
  // logo; all others render the two thin accent stripes. Kept as a
  // small helper so all three slide layouts below share one footer.
  const renderFooter = () =>
    template.footerStyle === 'logoBar' ? (
      <div
        className="bd-slide-logobar"
        style={{ background: `#${template.footerBarColor || template.accentLeft}` }}
      >
        {template.footerLogo && (
          <img
            className="bd-slide-logobar-img"
            src={template.footerLogo}
            alt=""
            aria-hidden="true"
          />
        )}
      </div>
    ) : (
      <div className="bd-slide-stripe">
        <span className="bd-slide-stripe-l" style={{ background: `#${template.accentLeft}` }} />
        <span className="bd-slide-stripe-r" style={{ background: `#${template.accentRight}` }} />
      </div>
    );

  // Bullet items for content slides (split body on newlines)
  const bulletLines =
    slide.type === 'content' || slide.type === 'agenda'
      ? slide.body
          .split('\n')
          .map((l) => l.replace(/^\s*[-•·]\s*/, '').trim())
          .filter(Boolean)
      : [];

  // ----- Visual slide -----
  if (slide.type === 'visual') {
    return (
      <div
        className={`bd-slide bd-slide-visual ${theme}`}
        style={{ background: bgColor }}
      >
        <span className="bd-slide-type-badge">{typeLabel}</span>
        {slide.chunk && (
          <span className="bd-slide-chunk">
            CHUNK {slide.chunk}
            {slide.chunkTitle && (
              <span className="bd-slide-chunk-title"> · {slide.chunkTitle}</span>
            )}
          </span>
        )}
        <div className="bd-visual-area" style={{ color: bodyColor, fontFamily: bodyFontStack }}>
          <span className="bd-visual-hint">
            {slide.body || 'Replace this with a photo, chart, or diagram'}
          </span>
        </div>
        {renderFooter()}
      </div>
    );
  }

  // ----- Section slide -----
  if (slide.type === 'section') {
    return (
      <div
        className={`bd-slide bd-slide-section ${theme}`}
        style={{ background: bgColor }}
      >
        <span className="bd-slide-type-badge">{typeLabel}</span>
        <div className="bd-section-body">
          {slide.chunk && (
            <div
              className="bd-section-eyebrow"
              style={{ fontFamily: bodyFontStack }}
            >
              SECTION {slide.chunk}
            </div>
          )}
          <div
            className="bd-section-title"
            style={{ fontFamily: headingFontStack, color: titleColor }}
          >
            {slide.title}
          </div>
        </div>
        {renderFooter()}
      </div>
    );
  }

  // ----- Default (title / objective / agenda / content / pause / qa / closing) -----
  return (
    <div
      className={`bd-slide bd-slide-${slide.type} ${theme}`}
      style={{ background: bgColor }}
    >
      <span className="bd-slide-type-badge">{typeLabel}</span>
      {slide.chunk && (
        <span className="bd-slide-chunk">
          CHUNK {slide.chunk}
          {slide.chunkTitle && (
            <span className="bd-slide-chunk-title"> · {slide.chunkTitle}</span>
          )}
        </span>
      )}
      <div className="bd-slide-body">
        {slide.title && (
          <div
            className="bd-slide-title"
            style={{ fontFamily: headingFontStack, color: titleColor }}
          >
            {slide.title}
          </div>
        )}
        {bulletLines.length > 0 ? (
          <ul
            className="bd-slide-bullets"
            style={{ fontFamily: bodyFontStack, color: bodyColor }}
          >
            {bulletLines.map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ul>
        ) : (
          slide.body && (
            <div
              className="bd-slide-text"
              style={{ fontFamily: bodyFontStack, color: bodyColor }}
            >
              {slide.body}
            </div>
          )
        )}
      </div>
      {renderFooter()}
    </div>
  );
}

function generateFilename(data) {
  const eventName =
    data.logistics?.eventName?.trim() ||
    data.objective?.built?.task?.trim() ||
    'presentation';
  const safe = eventName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50) || 'presentation';
  return `${safe}.pptx`;
}
